import { useState, useEffect, useCallback } from "react"
import { useShow } from "@/contexts/ShowContext"
import { useUser } from "@/contexts/UserContext"
import { useTasks } from "@/contexts/TasksContext"
import { useModal } from "@/contexts/ModalContext"
import { useWebSocket } from "@/hooks/useWebSocket"
import useKagamiBridge from "@/hooks/useKagamiBridge"
import { apiFetch } from "@/lib/api"
import { toast } from "sonner"
import dayjs from "dayjs"

import Loader from "@/components/loader"
import { Input } from "@/components/ui/shadcn/input"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/shadcn/table"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuCheckboxItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/shadcn/dropdown-menu"
import TaskModal from "@/components/modals/TaskModal"
import ConfirmModal from "@/components/modals/ConfirmModal"

import { FiX, FiCheck, FiAlertCircle, FiClock, FiTrash2, FiMoreHorizontal, FiChevronDown } from "react-icons/fi"
import { FaCog } from "react-icons/fa"
import { FaAngleLeft, FaAngleRight, FaAngleDoubleLeft } from "react-icons/fa"
import { TbCancel } from "react-icons/tb"

const MANAGER_ROLE = 30
const PAGE_SIZE = 50
// User-facing state filter groups. "In Progress" groups several underlying states.
const STATE_GROUPS = [
    { value: 'in_progress', label: 'In Progress', states: ['scheduled', 'pending', 'in_progress'] },
    { value: 'completed', label: 'Completed', states: ['completed'] },
    { value: 'failed', label: 'Failed', states: ['failed'] },
]
const TYPE_OPTIONS = ['image', 'video', 'audio']

function StateBadge({ state }) {
    const map = {
        cancelled: { icon: <TbCancel className='w-4 h-4 text-gray-500' />, label: 'Cancelled' },
        scheduled: { icon: <FiClock className='w-4 h-4 text-gray-400' />, label: 'Scheduled' },
        completed: { icon: <FiCheck className='w-4 h-4 text-green-500' />, label: 'Completed' },
        failed: { icon: <FiAlertCircle className='w-4 h-4 text-red-500' />, label: 'Failed' },
        in_progress: { icon: <FaCog className='w-4 h-4 text-secondary-foreground/80 animate-spin' />, label: 'In progress' },
        pending: { icon: <FaCog className='w-4 h-4 text-secondary-foreground/80 animate-spin' />, label: 'Pending' },
    }
    const entry = map[state] || { icon: null, label: state }
    return (
        <span className='flex items-center text-secondary-foreground' title={entry.label}>
            {entry.icon}
        </span>
    )
}

export default function TasksPage() {
    const { show } = useShow()
    const { user } = useUser()
    const { refresh: refreshBadge } = useTasks()
    const { openModal } = useModal()
    const { cancelTask, deleteTask } = useKagamiBridge()

    const isManager = (user?.role ?? 0) >= MANAGER_ROLE

    const [tasks, setTasks] = useState([])
    const [loading, setLoading] = useState(true)
    const [page, setPage] = useState(1)
    const [totalPages, setTotalPages] = useState(0)
    const [total, setTotal] = useState(0)
    const [selectedStates, setSelectedStates] = useState(STATE_GROUPS.map((g) => g.value))
    const [selectedTypes, setSelectedTypes] = useState(TYPE_OPTIONS)
    const [search, setSearch] = useState('')

    const allStatesSelected = selectedStates.length === STATE_GROUPS.length
    const allTypesSelected = selectedTypes.length === TYPE_OPTIONS.length
    const [actioningId, setActioningId] = useState(null)

    const showId = show?._id

    const fetchTasks = useCallback(async ({ silent = false } = {}) => {
        if (!showId) return
        try {
            if (!silent) setLoading(true)
            const params = new URLSearchParams()
            params.set('show', showId)
            params.set('page', String(page))
            params.set('size', String(PAGE_SIZE))
            if (isManager) params.set('scope', 'all')
            if (!allStatesSelected) {
                const states = STATE_GROUPS.filter((g) => selectedStates.includes(g.value)).flatMap((g) => g.states)
                params.set('state', states.length ? states.join(',') : 'none')
            }
            if (!allTypesSelected) {
                params.set('type', selectedTypes.length ? selectedTypes.join(',') : 'none')
            }
            if (search) params.set('search', search)

            const response = await apiFetch(`/api/tasks?${params.toString()}`)
            if (!response.ok) throw new Error('Failed to fetch tasks')
            const data = await response.json()
            setTasks(data.tasks || [])
            setTotalPages(data.totalPages || 0)
            setTotal(data.total || 0)
        } catch (error) {
            console.error('Error fetching tasks:', error)
        } finally {
            if (!silent) setLoading(false)
        }
    }, [showId, page, isManager, selectedStates, selectedTypes, allStatesSelected, allTypesSelected, search])

    useEffect(() => {
        fetchTasks()
    }, [showId, page, selectedStates, selectedTypes])

    useEffect(() => {
        const debounced = setTimeout(() => {
            if (page > 1) {
                setPage(1)
            } else {
                fetchTasks()
            }
        }, 500)
        return () => clearTimeout(debounced)
    }, [search])

    useWebSocket(['task_updated', 'task_completed', 'task_failed', 'task_cancelled'], {
        onMessage: () => {
            fetchTasks({ silent: true })
            refreshBadge()
        },
    })

    const handleCancel = async (task) => {
        const confirmed = await openModal(<ConfirmModal title="Cancel Task" message="Are you sure you want to cancel this task?" />)
        if (!confirmed) return
        try {
            setActioningId(task._id)
            await cancelTask(task._id)
            toast.success('Task cancelled', { duration: 4000, position: 'top-center' })
            await fetchTasks({ silent: true })
            refreshBadge()
        } catch (error) {
            toast.error('Failed to cancel task', { description: error.message, duration: 5000, position: 'top-center' })
        } finally {
            setActioningId(null)
        }
    }

    const handleDelete = async (task) => {
        const confirmed = await openModal(<ConfirmModal title="Delete Task" message="Are you sure you want to delete this task?" />)
        if (!confirmed) return
        try {
            setActioningId(task._id)
            await deleteTask(task._id)
            toast.success('Task deleted', { duration: 4000, position: 'top-center' })
            await fetchTasks({ silent: true })
            refreshBadge()
        } catch (error) {
            toast.error('Failed to delete task', { description: error.message, duration: 5000, position: 'top-center' })
        } finally {
            setActioningId(null)
        }
    }

    const toggleState = (value, checked) => {
        setPage(1)
        setSelectedStates((prev) => checked ? Array.from(new Set([...prev, value])) : prev.filter((v) => v !== value))
    }

    const toggleType = (value, checked) => {
        setPage(1)
        setSelectedTypes((prev) => checked ? Array.from(new Set([...prev, value])) : prev.filter((v) => v !== value))
    }

    const ownerName = (task) => {
        if (!task.user || typeof task.user !== 'object') return '—'
        const name = `${task.user.first_name || ''} ${task.user.last_name || ''}`.trim()
        return name || task.user.email || '—'
    }

    const startIndex = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1
    const endIndex = Math.min(page * PAGE_SIZE, total)

    return (
        <div className='flex flex-1 flex-col overflow-hidden'>
            <div className='flex items-center justify-between md:px-10 px-2 h-20'>
                <h1 className='text-2xl font-bold border-l-4 border-primary pl-4'>Tasks</h1>
            </div>

            {/* Filter toolbar — search input + state / type dropdowns + pagination */}
            <div className='flex items-start md:items-center gap-1 md:px-10 px-2 pb-3 md:flex-row flex-col'>
                <div className='relative md:w-58 w-full'>
                    <Input
                        type='text'
                        placeholder='Search'
                        className={`text-xs ${search ? 'pr-9' : ''}`}
                        onChange={(e) => setSearch(e.target.value)}
                        value={search}
                    />
                    {search && (
                        <FiX
                            className='w-4 h-4 absolute right-2 top-1/2 -translate-y-1/2 text-secondary-foreground/50 hover:text-secondary-foreground cursor-pointer'
                            onClick={() => setSearch('')}
                        />
                    )}
                </div>

                <div className='flex items-center gap-1 flex-1 w-full'>
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <button className='inline-flex h-9 outline-none items-center gap-2 px-2 text-xs rounded-md border bg-background hover:bg-secondary text-foreground cursor-pointer'>
                                <span>State</span>
                                {!allStatesSelected ? (
                                    <span className='w-4 h-4 flex items-center justify-center text-[10px] bg-primary text-primary-foreground rounded-full leading-none'>
                                        {selectedStates.length}
                                    </span>
                                ) : <FiChevronDown className='w-4 h-4 text-secondary-foreground/60' />}
                            </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align='start' className='w-48'>
                            <button
                                type='button'
                                className='flex w-full items-center rounded-sm px-2 h-6 text-xs hover:bg-primary hover:text-primary-foreground cursor-pointer'
                                onClick={() => { setPage(1); setSelectedStates(STATE_GROUPS.map((g) => g.value)) }}
                            >
                                Select all
                            </button>
                            <DropdownMenuSeparator />
                            {STATE_GROUPS.map((g) => (
                                <DropdownMenuCheckboxItem
                                    key={g.value}
                                    checked={selectedStates.includes(g.value)}
                                    onCheckedChange={(checked) => toggleState(g.value, checked)}
                                    onSelect={(e) => e.preventDefault()}
                                    className='group text-xs h-6'
                                >
                                    <span className='flex-1'>{g.label}</span>
                                    <button
                                        type='button'
                                        className='ml-auto hidden group-hover:inline-flex normal-case text-[10px] px-1.5 py-0.5 cursor-pointer rounded-sm text-primary-foreground hover:bg-primary-foreground hover:text-primary'
                                        onClick={(e) => {
                                            e.preventDefault()
                                            e.stopPropagation()
                                            setPage(1)
                                            setSelectedStates([g.value])
                                        }}
                                    >
                                        Only
                                    </button>
                                </DropdownMenuCheckboxItem>
                            ))}
                        </DropdownMenuContent>
                    </DropdownMenu>

                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <button className='inline-flex h-9 outline-none items-center gap-2 px-2 text-xs rounded-md border bg-background hover:bg-secondary text-foreground cursor-pointer'>
                                <span>Type</span>
                                {!allTypesSelected ? (
                                    <span className='w-4 h-4 flex items-center justify-center text-[10px] bg-primary text-primary-foreground rounded-full leading-none'>
                                        {selectedTypes.length}
                                    </span>
                                ) : <FiChevronDown className='w-4 h-4 text-secondary-foreground/60' />}
                            </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align='start' className='w-48'>
                            <button
                                type='button'
                                className='flex w-full items-center rounded-sm px-2 h-6 text-xs hover:bg-primary hover:text-primary-foreground cursor-pointer'
                                onClick={() => { setPage(1); setSelectedTypes(TYPE_OPTIONS) }}
                            >
                                Select all
                            </button>
                            <DropdownMenuSeparator />
                            {TYPE_OPTIONS.map((t) => (
                                <DropdownMenuCheckboxItem
                                    key={t}
                                    checked={selectedTypes.includes(t)}
                                    onCheckedChange={(checked) => toggleType(t, checked)}
                                    onSelect={(e) => e.preventDefault()}
                                    className='group capitalize text-xs h-6'
                                >
                                    <span className='flex-1'>{t}</span>
                                    <button
                                        type='button'
                                        className='ml-auto hidden group-hover:inline-flex normal-case text-[10px] px-1.5 py-0.5 cursor-pointer rounded-sm text-primary-foreground hover:bg-primary-foreground hover:text-primary'
                                        onClick={(e) => {
                                            e.preventDefault()
                                            e.stopPropagation()
                                            setPage(1)
                                            setSelectedTypes([t])
                                        }}
                                    >
                                        Only
                                    </button>
                                </DropdownMenuCheckboxItem>
                            ))}
                        </DropdownMenuContent>
                    </DropdownMenu>

                    {/* Pagination */}
                    <div className='flex-1 justify-end flex items-center gap-2'>
                        <span className='text-xs text-secondary-foreground/60 tabular-nums md:block hidden'>{`${startIndex}-${endIndex} of ${total}`}</span>
                        <div className='flex items-center border rounded-md h-9 overflow-hidden'>
                            <button
                                type='button'
                                onClick={() => setPage(1)}
                                disabled={page === 1}
                                title='First page'
                                className='inline-flex h-9 px-1 outline-none items-center justify-center bg-background hover:bg-secondary text-secondary-foreground cursor-pointer disabled:opacity-40 disabled:cursor-default disabled:hover:bg-background'
                            >
                                <FaAngleDoubleLeft className='w-3 h-3' />
                            </button>
                            <button
                                type='button'
                                onClick={() => setPage(Math.max(1, page - 1))}
                                disabled={page === 1}
                                title='Previous page'
                                className='inline-flex h-9 px-1 outline-none items-center justify-center bg-background hover:bg-secondary text-secondary-foreground cursor-pointer disabled:opacity-40 disabled:cursor-default disabled:hover:bg-background'
                            >
                                <FaAngleLeft className='w-3 h-3' />
                            </button>
                            <div className='text-xs text-secondary-foreground tabular-nums flex items-center px-2'>
                                <span>{page}</span>
                                <span>/{totalPages || 1}</span>
                            </div>
                            <button
                                type='button'
                                onClick={() => setPage(Math.min(totalPages, page + 1))}
                                disabled={page >= totalPages}
                                title='Next page'
                                className='inline-flex h-9 px-1 outline-none items-center justify-center bg-background hover:bg-secondary text-secondary-foreground cursor-pointer disabled:opacity-40 disabled:cursor-default disabled:hover:bg-background'
                            >
                                <FaAngleRight className='w-3 h-3' />
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className='flex flex-col flex-1 overflow-y-auto relative'>
                {!loading && tasks.length === 0 ? (
                    <div className='flex flex-col items-center justify-center flex-1 gap-4 text-secondary-foreground'>
                        <p>No tasks found.</p>
                    </div>
                ) : (
                    <Table>
                        <TableHeader className='bg-card text-secondary-foreground'>
                            <TableRow>
                                <TableHead className='pl-10 text-secondary-foreground/70'></TableHead>
                                <TableHead className='text-secondary-foreground/70'>Model</TableHead>
                                <TableHead className='text-secondary-foreground/70'>Type</TableHead>
                                <TableHead className='text-secondary-foreground/70'>Prompt</TableHead>
                                {isManager && <TableHead className='text-secondary-foreground/70'>Owner</TableHead>}
                                <TableHead className='text-secondary-foreground/70'>Status</TableHead>
                                <TableHead className='text-secondary-foreground/70'>Created</TableHead>
                                <TableHead className='w-12'></TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {tasks.filter(t => t && t.state).map((task) => {
                                const isActive = ['pending', 'in_progress', 'scheduled'].includes(task.state)
                                const canClear = ['failed', 'scheduled', 'cancelled'].includes(task.state)
                                const prompt = task.request_body?.payload?.prompt
                                const type = task.mediaType || task.model?.output?.media_type
                                return (
                                    <TableRow key={task._id} className={`cursor-pointer ${actioningId === task._id ? 'opacity-30 animate-pulse pointer-events-none' : ''}`} onClick={() => openModal(<TaskModal task={task} />, 'side')}>
                                        <TableCell className='pl-10 whitespace-nowrap'><StateBadge state={task.state} /></TableCell>
                                        <TableCell className='whitespace-nowrap'>
                                            <span className='text-[10px] bg-secondary/60 rounded-sm px-1 py-0.5 text-secondary-foreground'>{task.model?.name || '—'}</span>
                                        </TableCell>
                                        <TableCell className='whitespace-nowrap capitalize text-secondary-foreground/70 text-xs'>{type || '—'}</TableCell>
                                        <TableCell className='max-w-xs'>
                                            <span className='text-secondary-foreground/80 text-xs line-clamp-2'>{prompt || '—'}</span>
                                        </TableCell>
                                        {isManager && <TableCell className='whitespace-nowrap text-secondary-foreground/70 text-xs'>{ownerName(task)}</TableCell>}
                                        <TableCell className='max-w-[200px] text-secondary-foreground/70 text-xs'>
                                            <span className='line-clamp-2'>{task.status || '—'}</span>
                                        </TableCell>
                                        <TableCell className='whitespace-nowrap text-secondary-foreground/70 text-xs'>{dayjs(task.createdAt).format('MMM D, YYYY h:mm A')}</TableCell>
                                        <TableCell className='pr-10'>
                                            <DropdownMenu>
                                                <DropdownMenuTrigger onClick={(e) => e.stopPropagation()}>
                                                    <div className='p-2 rounded-md text-secondary-foreground/50 hover:text-secondary-foreground hover:bg-secondary'>
                                                        <FiMoreHorizontal className='w-4 h-4' />
                                                    </div>
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                                                    {isActive && (
                                                        <DropdownMenuItem onClick={() => handleCancel(task)}>
                                                            <TbCancel className='w-4 h-4' />
                                                            Cancel
                                                        </DropdownMenuItem>
                                                    )}
                                                    {canClear && (
                                                        <DropdownMenuItem onClick={() => handleDelete(task)} className='text-red-500 focus:text-primary-foreground group'>
                                                            <FiTrash2 className='w-4 h-4 text-red-500 group-hover:text-primary-foreground' />
                                                            Clear
                                                        </DropdownMenuItem>
                                                    )}
                                                </DropdownMenuContent>
                                            </DropdownMenu>
                                        </TableCell>
                                    </TableRow>
                                )
                            })}
                        </TableBody>
                    </Table>
                )}
                {loading && <Loader />}
            </div>
        </div>
    )
}
